import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Camera, ChevronDown, X, ChevronLeft, ChevronRight, Play } from 'lucide-react';

const Gallery = () => {
  const { t } = useLanguage();
  const [showAll, setShowAll] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  // TukTuk gallery media — images first, then videos at the end.
  const galleryItems = [
    {
      type: 'image',
      url: '/assets/gallery-1.jpg',
      alt: 'TukTuk Kefalonia - Front view with driver'
    },
    {
      type: 'image',
      url: '/assets/gallery-2.jpg',
      alt: 'TukTuk experience at the scenic coastline'
    },
    {
      type: 'image',
      url: '/assets/gallery-4.jpg',
      alt: 'View from inside TukTuk overlooking the sea'
    },
    {
      type: 'image',
      url: '/assets/gallery-5.jpg',
      alt: 'TukTuk on scenic Kefalonia roads'
    },
    {
      type: 'image',
      url: '/assets/gallery-6.jpg',
      alt: 'Wine tasting tour experience with TukTuk'
    },
    {
      type: 'image',
      url: '/assets/gallery-customers.jpg',
      alt: 'Happy customers enjoying TukTuk tour'
    },
    {
      type: 'image',
      url: '/assets/gallery-7.jpg',
      alt: 'TukTuk Kefalonia parked by the lagoon promenade'
    },
    {
      type: 'image',
      url: '/assets/gallery-8.jpg',
      alt: 'TukTuk Kefalonia at the Argostoli port'
    },
    {
      type: 'image',
      url: '/assets/gallery-9.jpg',
      alt: 'TukTuk Kefalonia by the European flags pavilion'
    },
    {
      type: 'image',
      url: '/assets/gallery-10.jpg',
      alt: 'TukTuk Kefalonia at the port with cruise ship'
    },
    {
      type: 'video',
      url: '/assets/gallery-video-1.mp4',
      poster: '/assets/gallery-video-1-poster.jpg',
      alt: 'TukTuk Kefalonia Tour Video'
    },
    {
      type: 'video',
      url: '/assets/gallery-video-2.mp4',
      poster: '/assets/gallery-video-2-poster.jpg',
      alt: 'TukTuk Kefalonia Experience Video'
    }
  ];

  // Show only 8 items on desktop initially (2 rows of 4)
  const displayedItems = showAll ? galleryItems : galleryItems.slice(0, 8);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === galleryItems.length - 1 ? 0 : prev + 1));
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setLightboxOpen(false);
      } else if (e.key === 'ArrowLeft') {
        setCurrentIndex((prev) => (prev === 0 ? galleryItems.length - 1 : prev - 1));
      } else if (e.key === 'ArrowRight') {
        setCurrentIndex((prev) => (prev === galleryItems.length - 1 ? 0 : prev + 1));
      }
    };
    if (!lightboxOpen) return undefined;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, galleryItems.length]);

  return (
    <section id="gallery" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-amber-100 rounded-full mb-4">
            <Camera className="w-5 h-5 text-amber-600" />
            <span className="text-amber-800 font-semibold text-sm uppercase tracking-wide">Gallery</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {t.gallery.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t.gallery.subtitle}
          </p>
        </div>

        {/* Gallery Grid - Smaller items */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {displayedItems.map((item, index) => (
            <div 
              key={item.url}
              onClick={() => openLightbox(index)}
              className="group relative overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-all duration-300 aspect-square cursor-pointer"
            >
              {item.type === 'image' ? (
                <img 
                  src={item.url}
                  alt={item.alt}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                />
              ) : (
                <div className="relative w-full h-full">
                  <img
                    src={item.poster}
                    alt={item.alt}
                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  {/* Play icon overlay for videos */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                    <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                      <Play className="w-7 h-7 text-gray-900 ml-1" fill="currentColor" />
                    </div>
                  </div>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </div>
          ))}
        </div>

        {/* Lightbox Modal */}
        {lightboxOpen && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 p-4"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Close"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* Previous button */}
            <button
              onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Previous"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>

            {/* Next button */}
            <button
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Next"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>

            {/* Content */}
            <div 
              className="relative max-w-7xl max-h-[90vh] w-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {galleryItems[currentIndex].type === 'image' ? (
                <img 
                  src={galleryItems[currentIndex].url}
                  alt={galleryItems[currentIndex].alt}
                  className="max-w-full max-h-[90vh] object-contain rounded-lg"
                />
              ) : (
                <video 
                  key={galleryItems[currentIndex].url}
                  src={galleryItems[currentIndex].url}
                  poster={galleryItems[currentIndex].poster}
                  controls
                  autoPlay
                  className="max-w-full max-h-[90vh] object-contain rounded-lg"
                >
                  Your browser does not support the video tag.
                </video>
              )}
            </div>

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-white text-sm">
              {currentIndex + 1} / {galleryItems.length}
            </div>
          </div>
        )}

        {/* Show More Button - Only on desktop when there are more items */}
        {!showAll && galleryItems.length > 8 && (
          <div className="mt-8 text-center">
            <button
              onClick={() => setShowAll(true)}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-all duration-300 transform hover:scale-105 shadow-md hover:shadow-lg"
            >
              <span>View More Photos & Videos</span>
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>
        )}

        {/* Show Less Button - After expanding */}
        {showAll && galleryItems.length > 8 && (
          <div className="mt-8 text-center">
            <button
              onClick={() => {
                setShowAll(false);
                // Scroll back to gallery section
                document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="inline-flex items-center space-x-2 px-8 py-4 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold rounded-lg transition-all duration-300"
            >
              <span>Show Less</span>
              <ChevronDown className="w-5 h-5 transform rotate-180" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
